import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import ReceiptIcon from "@material-ui/icons/Receipt";
import { userSignOut } from "actions/Auth";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getAxios } from "../../api/Axios";
import { invoiceApi } from "../../api/Urls";
import CardBox from "../../components/CardBox/index";
import CircularProgress from "../../components/CircularProgress";
import CustomActions from "../../components/CustomPagination/CustomActions";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import PersianPrice from "../Header/PersianPrice";
import toFarsiNumber from "util/toFarsiNumber";
import moment from "jalali-moment";
import InvoiceDetails from "./InvoiceDetails";
import useWindowSize from "../../hooks/useWindowsSize";
import ResponsiveTableBody from "components/ResponsiveTableBody/ResponsiveTableBody";
import "./Invoices.scss";
import { useTranslation } from "react-i18next";
import TableHeader from "components/TableHeader/TableHeader";
import { useSnackbar } from "notistack";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  table: {
    minWidth: 850,
    overflow: "hidden",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  actions: {
    display: "flex",
  },
  tableCellLeftAlignment: {
    textAlign: "right !important",
  },
  location: {
    color: "#ffb200",
    marginRight: "1rem",
  },
  phone: {
    color: "blue",
    marginRight: "1rem",
  },
  delete: {
    color: "red",
  },
  transactionSuccess: {
    display: "flex",
    width: "fit-content",
    backgroundColor: "rgba(0, 150, 136,0.1)",
    borderRadius: " 0.6em",
    padding: "0.5em ",
    margin: "0 auto",
    color: "rgba(0, 186, 186, 1)",
  },
  transactionFailed: {
    display: "flex",
    width: "fit-content",
    backgroundColor: "rgba(244,67,54, 0.1)",
    borderRadius: "0.6em",
    padding: "0.3em 0.6em ",
  },
  invoiceStatus: {
    display: "flex",
    width: "fit-content",
    backgroundColor: "rgba(0, 150, 136,0.1)",
    borderRadius: "0.6em",
    padding: "0.3em 0.6em ",
    margin: "0 auto",
    color: "rgb(48 152 243)",
  },
  detailBackground: {
    backgroundColor: "rgba(231, 236, 244, 0.9)",
    borderRadius: "0.6em",
    padding: "0.3em 0.6em ",
    cursor: "pointer",
  },
  noTransaction: {
    width: "100%",
    height: "50vh",
  },
  noTransactionItem: {
    display: "flex",
  },
  rial: {
    padding: "0 5px",
  },
  tableHeader: {
    textAlign: "center",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  iconDirectionLtr: {
    flexDirection: "row",
  },
}));

export default function Invoices() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const windowSize = useWindowSize();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [openSnack, setOpenSnack] = useState(false);
  const [detailData, setDetailData] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("pk");

  const headCells = [
    {
      id: "pk",
      isNeedSort: true,
      directionCenter: true,
      label: t("invoices.invoice_no"),
    },
    {
      id: "price",
      isNeedSort: false,
      directionCenter: true,
      label: t("invoices.price"),
    },
    {
      id: "start_time",
      isNeedSort: true,
      directionCenter: true,
      label: t("invoices.start_date"),
    },
    {
      id: "end_date",
      isNeedSort: false,
      directionCenter: true,
      label: t("invoices.end_date"),
    },
    {
      id: "is_closed",
      isNeedSort: true,
      directionCenter: true,
      label: t("invoices.status"),
    },
    {
      id: "nothing",
      isNeedSort: false,
      directionCenter: true,
      label: "",
    },
  ];

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  useEffect(() => {
    getAxios(invoiceApi)
      .then((res) => {
        setData(
          res.data.invoice.sort((a, b) =>
            a.pk < b.pk ? 1 : b.pk < a.pk ? -1 : 0
          )
        );
        setLoading(false);
      })
      .catch((err) => {
        if (err?.response) {
          enqueueSnackbar(err.response.data.message, {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            style: { direction: i18n.language === "en" ? "ltr" : "rtl" },
          });
        } else {
          console.log(err);
        }
        if (err.response.status === 401) {
          dispatch(userSignOut());
        }
      });
  }, []);

  const getInvoiceDetails = (id) => {
    setOpen(true);
    setDetailsLoading(true);
    getAxios(`${invoiceApi}/${id}`)
      .then((res) => {
        if (res.status === 200) {
          setDetailData(res.data.invoice[0]);
          setDetailsLoading(false);
        }
      })
      .catch((err) => {
        if (err?.response) {
          enqueueSnackbar(err.response.data.message, {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            style: { direction: i18n.language === "en" ? "ltr" : "rtl" },
          });
        } else {
          console.log(err);
        }
        setOpen(false);
        setDetailsLoading(false);
      });
  };

  const handleSnackbar = () => {
    setOpenSnack(false);
    setError("");
  };

  const handleRequestClose = () => {
    setOpen(false);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);

  return (
    <div
      className={`row animated slideInUpTiny animation-duration-3 ${
        i18n.dir() === "rtl" ? "uptin-table" : "uptin-table-ltr"
      }`}
    >
      <CardBox styleName="col-12" cardStyle=" p-0" headerOutside>
        <TableContainer
          style={{
            borderRadius: "1.5rem",
            overflow: "hidden",
            direction: i18n.dir() === "rtl" ? "rtl" : "ltr",
          }}
          component={Paper}
        >
          {" "}
          {loading ? (
            <CircularProgress />
          ) : data.length > 0 ? (
            <>
              {windowSize.width > 430 ? (
                <Table
                  className={classes.table}
                  aria-label="custom pagination table"
                >
                  <TableHeader
                    classes={classes}
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    rowCount={data.length}
                    headCells={headCells}
                  />
                  <>
                    <TableBody>
                      <>
                        {stableSort(data, getComparator(order, orderBy))
                          .slice(
                            rowsPerPage > 0 ? page * rowsPerPage : 0,
                            rowsPerPage > 0
                              ? page * rowsPerPage + rowsPerPage
                              : data.length
                          )
                          .map((row, index) => {
                            return (
                              <TableRow key={row.pk}>
                                <TableCell align="center">
                                  {i18n.language === "fa"
                                    ? toFarsiNumber(row.pk) || "-"
                                    : row.pk || "-"}
                                </TableCell>
                                <TableCell align="center">
                                  <span className={classes.transactionSuccess}>
                                    {i18n.language === "fa" ? (
                                      <PersianPrice
                                        number={Math.floor(row.orders[0].total)}
                                      />
                                    ) : (
                                      Math.floor(row.orders[0].total)
                                    )}
                                    <span className={classes.rial}>
                                      {t("invoices.rial")}
                                    </span>
                                  </span>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  style={{ direction: "ltr" }}
                                >
                                  {i18n.language === "fa"
                                    ? toFarsiNumber(
                                        moment(row.start_time, "YYYY/MM/DD")
                                          .locale("fa")
                                          .format("YYYY/MM/DD")
                                      )
                                    : moment(row.start_time, "YYYY/MM/DD")
                                        .locale("en")
                                        .format("YYYY/MM/DD")}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  style={{ direction: "ltr" }}
                                >
                                  {i18n.language === "fa"
                                    ? toFarsiNumber(
                                        moment(row.end_time, "YYYY/MM/DD")
                                          .locale("fa")
                                          .format("YYYY/MM/DD")
                                      )
                                    : moment(row.end_time, "YYYY/MM/DD")
                                        .locale("en")
                                        .format("YYYY/MM/DD")}
                                </TableCell>
                                <TableCell align="center">
                                  <span className={classes.invoiceStatus}>
                                    {row.is_closed
                                      ? t("invoices.closed")
                                      : t("invoices.current")}
                                  </span>
                                </TableCell>
                                <TableCell align="center">
                                  <span
                                    style={{ cursor: "pointer" }}
                                    className={classes.detailBackground}
                                    onClick={() => getInvoiceDetails(row.uuid)}
                                  >
                                    {t("invoices.details")}
                                  </span>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        {emptyRows > 0 && (
                          <TableRow style={{ height: 48 * emptyRows }}>
                            <TableCell colSpan={6} />
                          </TableRow>
                        )}
                      </>
                    </TableBody>
                    <TableFooter>
                      <TableRow
                        style={{
                          overflow: "hidden",
                          position: "relative",
                          width: "100%",
                        }}
                      >
                        <CustomActions
                          page={page}
                          setPage={setPage}
                          dataLength={data.length}
                          rowsPerPage={rowsPerPage}
                        />
                        <CustomPagination setRowsPerPage={setRowsPerPage} />
                        <TablePagination
                          rowsPerPageOptions={[
                            5,
                            10,
                            20,
                            30,
                            { label: t("invoices.all"), value: -1 },
                          ]}
                          // colSpan={5}
                          count={data.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          SelectProps={{
                            inputProps: {
                              "aria-label": "Rows per page",
                            },
                            native: true,
                          }}
                          labelRowsPerPage={t("invoices.rows_per_page")}
                          onChangePage={handleChangePage}
                          onChangeRowsPerPage={handleChangeRowsPerPage}
                          // ActionsComponent={TablePaginationActions}
                        />
                      </TableRow>
                    </TableFooter>
                  </>
                </Table>
              ) : (
                <Table
                  style={{
                    border: "2px solid #e7ecf4",
                    borderRadius: "1.6rem",
                  }}
                  aria-label="simple table"
                >
                  <TableBody>
                    {(rowsPerPage > 0
                      ? data.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                      : data
                    ).map((row, index) => (
                      <ResponsiveTableBody
                        row={row}
                        tableKeys={[
                          t("invoices.invoice_no"),
                          t("invoices.price"),
                          t("invoices.start_date"),
                          t("invoices.end_date"),
                          t("invoices.status"),
                          "",
                        ]}
                        tableValues={[
                          "pk",
                          "orders[0].product.price",
                          "start_time",
                          "end_time",
                          "is_closed",
                          "",
                        ]}
                        actions={
                          <span
                            style={{ cursor: "pointer" }}
                            className={classes.detailBackground}
                            onClick={() => getInvoiceDetails(row.uuid)}
                          >
                            {t("invoices.details")}
                          </span>
                        }
                      />
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow
                      style={{
                        overflow: "hidden",
                        position: "relative",
                        width: "100%",
                      }}
                    >
                      <TableCell colSpan={3} align="center">
                        <CustomActions
                          page={page}
                          setPage={setPage}
                          dataLength={data.length}
                          rowsPerPage={rowsPerPage}
                        />
                        <CustomPagination setRowsPerPage={setRowsPerPage} />
                        <TablePagination
                          rowsPerPageOptions={[
                            5,
                            10,
                            20,
                            30,
                            { label: t("invoices.all"), value: -1 },
                          ]}
                          // colSpan={3}
                          count={data.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          SelectProps={{
                            inputProps: {
                              "aria-label": "Rows per page",
                            },
                            native: true,
                          }}
                          labelRowsPerPage={t("invoices.rows_per_page")}
                          onChangePage={handleChangePage}
                          onChangeRowsPerPage={handleChangeRowsPerPage}
                          // ActionsComponent={TablePaginationActions}
                        />
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              )}
            </>
          ) : (
            <div className="no-users">
              <div
                style={{
                  padding: "100px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  opacity: "0.5",
                  color: "gray",
                  fontSize: "2rem",
                }}
              >
                <ReceiptIcon style={{ fontSize: "4rem" }} />
                {t("invoices.no_invoices")}
              </div>
            </div>
          )}
        </TableContainer>
      </CardBox>
      {open && (
        <InvoiceDetails
          handleSnackbar={handleSnackbar}
          open={open}
          openSnack={openSnack}
          error={error}
          handleRequestClose={handleRequestClose}
          detailData={detailData}
          detailsLoading={detailsLoading}
        />
      )}
    </div>
  );
}
